import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import { formatDistance } from "date-fns";
import * as React from "react";
import { FaMeta } from "react-icons/fa6";
import { FaLinkedinIn } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { LuPlug } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import {
  ConversionAction,
  ConversionActionPlatform,
  getConversionActions,
} from "../api/conversion-action";
import {
  LinkedinAgentStats,
  getLinkedinAgentStats,
} from "../api/linkedin-agent";
import { getSuppressionAudiences } from "../api/suppression-audience";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import { StatusPill, StatusPillTone } from "../components/status-pill";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useWorkspaceURL,
} from "../hooks";
import { brand } from "../lib/brand";
import {
  EXPIRY_WARNING_DAYS,
  daysUntilExpiry,
} from "../lib/integration-expiry";
import {
  loadIntegrations,
  selectCurrentWorkspace,
} from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";
import { SuppressionAudience } from "../types/suppression-audience";

export default function PlatformsDashboardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const slug = useAppSelector((s) => s.workspace.slug);
  const integrations = useAppSelector((s) => s.workspace.integrations);
  const isLoadingIntegrations = useAppSelector(
    (s) => s.workspace.isLoadingIntegrations,
  );
  const workspaceURL = useWorkspaceURL();
  useBreadcrumbs([{ label: "Dashboard" }]);

  const [conversionActions, setConversionActions] = React.useState<
    ConversionAction[]
  >([]);
  const [agentStats, setAgentStats] = React.useState<LinkedinAgentStats | null>(
    null,
  );
  const [suppressionAudiences, setSuppressionAudiences] = React.useState<
    SuppressionAudience[]
  >([]);
  const [isLoading, withLoading] = useLoading(true);

  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  const findIntegration = React.useCallback(
    (id: string) => integrations.find((i) => i.id === id),
    [integrations],
  );
  const linkedinConnected =
    findIntegration("linkedin")?.status === IntegrationStatus.connected;

  React.useEffect(() => {
    if (!slug) return;
    withLoading(async () => {
      const fetches: Promise<void>[] = [
        getConversionActions(slug).then(setConversionActions),
      ];
      if (workspace.type === "dtc") {
        fetches.push(
          getSuppressionAudiences(slug).then(setSuppressionAudiences),
        );
      }
      await Promise.all(fetches).catch(() => {});
    });
  }, [slug, withLoading, workspace.type]);

  React.useEffect(() => {
    if (!slug || !linkedinConnected) return;
    getLinkedinAgentStats(slug)
      .then(setAgentStats)
      .catch(() => {});
  }, [linkedinConnected, slug]);

  // Addie's dashboard lists what the customer has actually hooked up, so a
  // card earns its place by having an integration behind it. A broken one
  // still counts: its pill is the only place the breakage shows.
  const hasIntegration = (id: string) => {
    const status = findIntegration(id)?.status;
    return status !== undefined && status !== IntegrationStatus.disconnected;
  };
  const isAddie = brand.id === "addie";
  const showLinkedin = isAddie
    ? hasIntegration("linkedin")
    : workspace.type === "b2b";
  const showGoogle = isAddie ? hasIntegration("google-ads") : true;
  const showMeta = isAddie ? hasIntegration("facebook") : true;

  const platformStats = (platform: ConversionActionPlatform) => {
    const actions = conversionActions.filter((a) => a.platform === platform);
    const events24 = actions.reduce((sum, a) => sum + a["recent-events"], 0);
    const lastSentAt = actions.reduce<string | null>(
      (last, a) =>
        a["last-sent-at"] && (!last || a["last-sent-at"] > last)
          ? a["last-sent-at"]
          : last,
      null,
    );
    return [
      {
        label: "Actions (24h)",
        value: actions.length ? String(events24) : "—",
      },
      { label: "Last", value: lastSentAt ? timeAgo(lastSentAt) : "—" },
    ];
  };

  if (!showLinkedin && !showGoogle && !showMeta) {
    return (
      <Page loading={isLoading || isLoadingIntegrations}>
        <Stack flex="1" justify="center">
          <EmptyState
            icon={<IconCircle Icon={LuPlug} size="md" variant="flat" />}
            title="No platforms connected"
            description={`Connect an ad platform so ${brand.productName} can start valuing your conversions.`}
          >
            <Button
              onClick={() => navigate(workspaceURL("settings/integrations"))}
            >
              Go to Integration Settings
            </Button>
          </EmptyState>
        </Stack>
      </Page>
    );
  }

  return (
    <Page loading={isLoading || isLoadingIntegrations}>
      <Stack flex="1" gap="6">
        <Stack gap="4">
          <Eyebrow>Platforms</Eyebrow>
          <Stack gap="4">
            {showLinkedin && (
              <PlatformCard
                icon={
                  <Box color="brand.linkedin">
                    <FaLinkedinIn size="20" />
                  </Box>
                }
                label="LinkedIn"
                pill={integrationPill(findIntegration("linkedin"))}
                stats={[
                  {
                    label: "Actions (24h)",
                    value:
                      linkedinConnected && agentStats
                        ? String(agentStats["actions-24h"])
                        : "—",
                  },
                  {
                    label: "Last",
                    value:
                      linkedinConnected && agentStats?.["last-action-at"]
                        ? timeAgo(agentStats["last-action-at"])
                        : "—",
                  },
                ]}
                onClick={() =>
                  navigate(
                    linkedinConnected
                      ? workspaceURL("linkedin-ad-accounts")
                      : workspaceURL("settings/integrations"),
                  )
                }
              />
            )}
            {showGoogle && (
              <PlatformCard
                icon={<FcGoogle size="20" />}
                label="Google"
                pill={integrationPill(findIntegration("google-ads"))}
                stats={platformStats(ConversionActionPlatform.GOOGLE_ADS)}
                onClick={() => navigate(workspaceURL("google-ads-accounts"))}
              />
            )}
            {showMeta && (
              <PlatformCard
                icon={
                  <Box color="brand.meta">
                    <FaMeta size="20" />
                  </Box>
                }
                label="Meta"
                pill={integrationPill(findIntegration("facebook"))}
                stats={platformStats(ConversionActionPlatform.META)}
                onClick={() =>
                  navigate(workspaceURL("conversion-actions/meta"))
                }
              />
            )}
          </Stack>
        </Stack>

        {workspace.type === "dtc" && (
          <Stack gap="4">
            <Eyebrow>Exclusion Audiences</Eyebrow>
            <Stack gap="4">
              {suppressionAudiences.map((a) => (
                <PlatformCard
                  key={a.id}
                  label={a.name}
                  pill={
                    <StatusPill tone={a.status === "active" ? "green" : "gray"}>
                      {a.status === "active" ? "Active" : "Inactive"}
                    </StatusPill>
                  }
                  stats={[
                    {
                      label: "Contacts (24h)",
                      value: String(a["recent-contacts"]),
                    },
                    {
                      label: "Last sync",
                      value: a["last-synced-at"]
                        ? timeAgo(a["last-synced-at"])
                        : "—",
                    },
                  ]}
                  onClick={() =>
                    navigate(workspaceURL("suppression-audiences"))
                  }
                />
              ))}
              {suppressionAudiences.length === 0 && (
                <Stack
                  background="white"
                  borderRadius="l3"
                  boxShadow="card"
                  cursor={"pointer" as any}
                  onClick={() =>
                    navigate(workspaceURL("suppression-audiences"))
                  }
                  paddingX="6"
                  paddingY="5"
                  _hover={{ background: "bg.subtle" }}
                >
                  <Text color="fg.muted" fontSize="sm">
                    No exclusion audiences yet — create one
                  </Text>
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Page>
  );
}

const timeAgo = (date: string) =>
  formatDistance(new Date(date), new Date(), { addSuffix: true });

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      color="fg.muted"
      fontFamily="inter"
      fontSize="xs"
      fontWeight="medium"
      letterSpacing={"0.08em" as any}
      lineHeight={"18px" as any}
      textTransform="uppercase"
    >
      {children}
    </Text>
  );
}

interface IntegrationLike {
  status: IntegrationStatus;
  "expires-at"?: string | null;
}

function integrationPill(integration: IntegrationLike | undefined) {
  if (!integration) {
    return <StatusPill tone="yellow">Not connected</StatusPill>;
  }
  const days = daysUntilExpiry(integration["expires-at"] ?? null);
  if (
    integration.status === IntegrationStatus.connected &&
    days !== null &&
    days <= EXPIRY_WARNING_DAYS
  ) {
    return (
      <StatusPill tone="amber">
        {days <= 0 ? "Expired" : `Expires in ${days}d`}
      </StatusPill>
    );
  }
  const config: Partial<
    Record<IntegrationStatus, { tone: StatusPillTone; label: string }>
  > = {
    [IntegrationStatus.connected]: { tone: "green", label: "Connected" },
    [IntegrationStatus.connecting]: { tone: "blue", label: "Connecting" },
    [IntegrationStatus.broken]: { tone: "red", label: "Error" },
    [IntegrationStatus.disconnected]: {
      tone: "yellow",
      label: "Not connected",
    },
  };
  const entry = config[integration.status];
  if (!entry) {
    return <StatusPill tone="gray">{integration.status}</StatusPill>;
  }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>;
}

function PlatformCard({
  icon,
  label,
  pill,
  stats,
  onClick,
}: {
  icon?: React.ReactNode;
  label: string;
  pill: React.ReactNode;
  stats: { label: string; value: string }[];
  onClick: () => void;
}) {
  return (
    <Stack
      background="white"
      borderRadius="l3"
      boxShadow="card"
      cursor={"pointer" as any}
      gap={"18px" as any}
      onClick={onClick}
      paddingX="6"
      paddingY="5"
      _hover={{ background: "bg.subtle" }}
    >
      <HStack gap="3">
        {icon && (
          <HStack
            flexShrink="0"
            height={"22px" as any}
            justify="center"
            width={"22px" as any}
          >
            {icon}
          </HStack>
        )}
        <Text
          color="fg.ink"
          flex="1"
          fontSize="md"
          fontWeight="medium"
          lineHeight={"1.35" as any}
          truncate
        >
          {label}
        </Text>
        {pill}
      </HStack>
      <HStack gap="14" paddingLeft={"34px" as any}>
        {stats.map((stat) => (
          <Stack gap="0.5" key={stat.label} minWidth={"120px" as any}>
            <Text
              color="fg.muted"
              fontSize={"13px" as any}
              lineHeight={"18px" as any}
            >
              {stat.label}
            </Text>
            <Text
              color="fg.ink"
              fontSize={"15px" as any}
              fontVariantNumeric="tabular-nums"
              fontWeight="medium"
              lineHeight={"20px" as any}
            >
              {stat.value}
            </Text>
          </Stack>
        ))}
      </HStack>
    </Stack>
  );
}
